/**
 * Donor-facing phrases and deadlines used in the paid claim guide.
 * Keep these derived from the versioned tax-year config, never hardcoded
 * to a single year in the UI.
 */

import type { ClaimIncomeBand } from '@/types/claimTaxBack'

export type RatePhraseId =
  | 'basic'
  | 'intermediate'
  | 'higher'
  | 'additional'
  | 'advanced'
  | 'top'

/**
 * Short name for the donor's marginal rate, for scripts they read to HMRC.
 * Uses the band id for Scottish names (advanced / top) that share a percent
 * with a rest-of-UK band.
 */
export function ratePhraseIdForBand(band: ClaimIncomeBand): RatePhraseId {
  if (band.id.includes('intermediate')) return 'intermediate'
  if (band.id.includes('advanced')) return 'advanced'
  if (band.id.includes('top')) return 'top'
  if (band.id.includes('additional')) return 'additional'
  if (band.marginalRatePercent <= 20) return 'basic'
  if (band.marginalRatePercent <= 21) return 'intermediate'
  if (band.marginalRatePercent <= 42) return 'higher'
  return 'additional'
}

/**
 * Self Assessment online filing year for a UK tax year period key.
 * 2025-2026 (year ending 5 April 2026) is due 31 January 2027.
 */
export function selfAssessmentDeadlineYears(periodKey: string): {
  fileByYear: number
  amendByYear: number
} {
  const endYear = parseInt(periodKey.split('-')[1] ?? '', 10)
  if (!Number.isInteger(endYear)) {
    throw new Error(
      `Cannot derive Self Assessment deadlines from "${periodKey}"`
    )
  }
  return { fileByYear: endYear + 1, amendByYear: endYear + 2 }
}

/**
 * HMRC asks donors to keep Gift Aid records for at least 22 months after the
 * end of the tax year (gov.uk/donating-to-charity/keeping-records). A year
 * ending 5 April 2026 therefore runs to early February 2028, which GOV.UK
 * rounds to "the end of January". Returns that calendar year.
 */
export function recordsKeepUntilYear(periodKey: string): number {
  const endYear = parseInt(periodKey.split('-')[1] ?? '', 10)
  if (!Number.isInteger(endYear)) {
    throw new Error(`Cannot derive record keeping date from "${periodKey}"`)
  }
  return endYear + 2
}
